import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/fr';
import { mealAPI } from '../services/api';
import { Meal } from '../types';
import MealModal from './MealModal';
import MobileMenu from './MobileMenu';
import WeekView from './meals/WeekView';
import MonthView from './meals/MonthView';
import { Container } from './layout/Container';

moment.locale('fr');

interface MealPlannerCalendarProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

const MealPlannerCalendar: React.FC<MealPlannerCalendarProps> = ({ activeView, onViewChange }) => {
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [weekStartStr, setWeekStartStr] = useState(() =>
    moment().startOf('isoWeek').format('YYYY-MM-DD')
  );
  const [currentMonthStr, setCurrentMonthStr] = useState(() =>
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    try {
      let startDate: string, endDate: string;
      if (viewMode === 'week') {
        startDate = weekStartStr;
        endDate = moment(weekStartStr).endOf('isoWeek').format('YYYY-MM-DD');
      } else {
        startDate = moment(currentMonthStr).startOf('isoWeek').format('YYYY-MM-DD');
        endDate = moment(currentMonthStr).endOf('month').endOf('isoWeek').format('YYYY-MM-DD');
      }
      const res = await mealAPI.getAll({ startDate, endDate });
      if (res.success && res.data) setMeals(res.data);
    } catch (err) {
      console.error('Error fetching meals:', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, weekStartStr, currentMonthStr]);

  useEffect(() => {
    fetchMeals();
  }, [fetchMeals]);

  const handleAddMeal = (dateString: string) => {
    setSelectedDate(dateString);
    setEditingMeal(null);
    setShowModal(true);
  };

  const handleEditMeal = (meal: Meal) => {
    setEditingMeal(meal);
    setSelectedDate(meal.dateString);
    setShowModal(true);
  };

  const handleMoveMeal = async (meal: Meal, newDateString: string) => {
    if (!meal._id) return;
    try {
      await mealAPI.update(meal._id, { ...meal, dateString: newDateString });
      fetchMeals();
    } catch (err) {
      console.error('Error moving meal:', err);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingMeal(null);
  };

  // Called by MealModal on save/delete (before onClose)
  const handleModalSave = () => {
    fetchMeals();
  };

  // Month view: click a day → navigate to that week
  const handleDayClick = (dateString: string) => {
    setWeekStartStr(moment(dateString).startOf('isoWeek').format('YYYY-MM-DD'));
    setViewMode('week');
  };

  const goToPrev = () => {
    if (viewMode === 'week') {
      setWeekStartStr(s => moment(s).subtract(1, 'week').format('YYYY-MM-DD'));
    } else {
      setCurrentMonthStr(s => moment(s).subtract(1, 'month').format('YYYY-MM-DD'));
    }
  };

  const goToNext = () => {
    if (viewMode === 'week') {
      setWeekStartStr(s => moment(s).add(1, 'week').format('YYYY-MM-DD'));
    } else {
      setCurrentMonthStr(s => moment(s).add(1, 'month').format('YYYY-MM-DD'));
    }
  };

  const goToToday = () => {
    if (viewMode === 'week') {
      setWeekStartStr(moment().startOf('isoWeek').format('YYYY-MM-DD'));
    } else {
      setCurrentMonthStr(moment().startOf('month').format('YYYY-MM-DD'));
    }
  };

  const headerTitle = viewMode === 'week'
    ? `${moment(weekStartStr).format('D MMM')} – ${moment(weekStartStr).endOf('isoWeek').format('D MMM YYYY')}`
    : moment(currentMonthStr).format('MMMM YYYY');

  const isCurrentPeriod = viewMode === 'week'
    ? weekStartStr === moment().startOf('isoWeek').format('YYYY-MM-DD')
    : currentMonthStr === moment().startOf('month').format('YYYY-MM-DD');

  return (
    <>
      <MobileMenu activeView={activeView} onViewChange={onViewChange} />

      <main className="py-6">
        <Container maxWidth="xl">

          {/* Controls bar */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            {/* Prev / Next */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={goToPrev}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 transition-colors text-lg font-medium"
                aria-label="Précédent"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goToNext}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 transition-colors text-lg font-medium"
                aria-label="Suivant"
              >
                ›
              </button>
            </div>

            {/* Period title */}
            <h2 className="text-base md:text-lg font-bold text-gray-900 capitalize flex-1 min-w-0 truncate">
              {headerTitle}
            </h2>

            {/* Today shortcut */}
            {!isCurrentPeriod && (
              <button
                type="button"
                onClick={goToToday}
                className="text-sm text-primary hover:underline font-medium flex-shrink-0"
              >
                Aujourd'hui
              </button>
            )}

            {/* View toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm flex-shrink-0">
              <button
                type="button"
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Semaine
              </button>
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${
                  viewMode === 'month'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Mois
              </button>
            </div>
          </div>

          {/* Calendar area */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-xl">
                <span className="text-gray-400 font-medium text-sm">Chargement...</span>
              </div>
            )}

            {viewMode === 'week' ? (
              <WeekView
                weekStartStr={weekStartStr}
                meals={meals}
                onAddMeal={handleAddMeal}
                onEditMeal={handleEditMeal}
                onMoveMeal={handleMoveMeal}
              />
            ) : (
              <MonthView
                currentMonthStr={currentMonthStr}
                meals={meals}
                onDayClick={handleDayClick}
              />
            )}
          </div>

        </Container>
      </main>

      <MealModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        meal={editingMeal}
        dateString={selectedDate || moment().format('YYYY-MM-DD')}
      />
    </>
  );
};

export default MealPlannerCalendar;
